/*
  SMA — Product page enhancements
  ---------------------------------------------------------------------------
  1. <sma-sticky-atc>  mobile sticky Add to cart bar, with three visibility
     modes driven by Theme settings > SMA Product Page.
  2. Price inside the Add to cart button, kept in sync with the selected
     variant.
  3. Mobile gallery pagination dots.
  4. Size gate — no size is preselected, and Add to cart stays a disabled
     "Select size" until the shopper picks one. Mirrored onto the sticky bar.
  5. "Size Guide" link in the size option header, which forwards its click to
     the product popup block.

  Deliberately self-contained: it watches the DOM rather than hooking Dawn's
  pub/sub, so it keeps working if product-info.js is updated.
*/
(function () {
  'use strict';

  /* ----------------------------------------------------------------------
     1. Sticky Add to cart bar
     ---------------------------------------------------------------------- */
  if (!customElements.get('sma-sticky-atc')) {
    customElements.define(
      'sma-sticky-atc',
      class SmaStickyAtc extends HTMLElement {
        connectedCallback() {
          this.mode = this.dataset.visibility || 'past_atc';
          this.sectionId = this.dataset.section;

          // Always mode needs no scroll tracking at all.
          if (this.mode === 'always') {
            this.toggle(true);
            return;
          }

          this.anchor =
            document.querySelector('#ProductInfo-' + this.sectionId + ' .product-form__buttons') ||
            document.getElementById('ProductSubmitButton-' + this.sectionId);

          // No anchor to track (or no IO support) — fall back to always on.
          if (!this.anchor || !('IntersectionObserver' in window)) {
            this.toggle(true);
            return;
          }

          this.observer = new IntersectionObserver(
            (entries) => {
              const entry = entries[entries.length - 1];
              if (this.mode === 'atc_hidden') {
                // Visible whenever the real button is off-screen, above or below.
                this.toggle(!entry.isIntersecting);
              } else {
                // past_atc — only once the real button has scrolled off the top.
                this.toggle(!entry.isIntersecting && entry.boundingClientRect.bottom <= 0);
              }
            },
            { threshold: 0 }
          );

          this.observer.observe(this.anchor);
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
        }

        toggle(visible) {
          this.classList.toggle('is-visible', !!visible);
        }
      }
    );
  }

  /* ----------------------------------------------------------------------
     2. Price in the Add to cart button + sticky bar state sync
     ---------------------------------------------------------------------- */

  // Reads the price currently shown in the PDP price block, honouring sale state.
  function currentPrice(sectionId) {
    const root = document.getElementById('price-' + sectionId);
    if (!root) return null;

    const wrapper = root.querySelector('.price');
    if (!wrapper || wrapper.classList.contains('price--sold-out')) return null;

    const node = wrapper.classList.contains('price--on-sale')
      ? root.querySelector('.price__sale .price-item--sale')
      : root.querySelector('.price__regular .price-item--regular');

    if (!node) return null;
    const text = node.textContent.trim();
    return text.length ? text : null;
  }

  function syncSection(sectionId, state) {
    if (state.busy) {
      // Don't drop this batch — replay it once the current pass settles.
      state.pending = true;
      return;
    }
    state.busy = true;
    state.pending = false;

    const price = currentPrice(sectionId);
    const mainButton = document.getElementById('ProductSubmitButton-' + sectionId);
    const mainLabel = mainButton ? mainButton.querySelector('span') : null;
    const disabled = mainButton ? mainButton.hasAttribute('disabled') : false;

    // Price chips (main button and/or sticky bar).
    document
      .querySelectorAll('[data-sma-atc-price][data-section="' + sectionId + '"]')
      .forEach(function (chip) {
        if (price && !disabled) {
          chip.textContent = price;
          chip.hidden = false;
        } else {
          chip.hidden = true;
        }
      });

    // Mirror the real button's label + disabled state onto the sticky bar,
    // and keep the sticky Buy Now form pointed at the selected variant.
    const realId = document.querySelector('#product-form-' + sectionId + ' input[name="id"]');

    document
      .querySelectorAll('.sma-sticky-atc[data-section="' + sectionId + '"]')
      .forEach(function (bar) {
        const button = bar.querySelector('.sma-sticky-atc__button');
        if (button) {
          if (disabled) button.setAttribute('disabled', 'disabled');
          else button.removeAttribute('disabled');
        }

        const label = bar.querySelector('.sma-sticky-atc__label');
        if (label && mainLabel) label.textContent = mainLabel.textContent.trim();

        const stickyId = bar.querySelector('input[name="id"]');
        if (stickyId && realId) stickyId.value = realId.value;
      });

    // Re-bind gallery dots in case the media gallery was re-rendered.
    document.querySelectorAll('[data-sma-gallery-dots]').forEach(initDots);

    // Let the observers settle before we listen again.
    window.requestAnimationFrame(function () {
      state.busy = false;
      if (state.pending) syncSection(sectionId, state);
    });
  }

  function initSection(sectionId) {
    const state = { busy: false, pending: false };
    const sync = function () {
      syncSection(sectionId, state);
    };

    const priceRoot = document.getElementById('price-' + sectionId);
    if (priceRoot) {
      new MutationObserver(sync).observe(priceRoot, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    const mainButton = document.getElementById('ProductSubmitButton-' + sectionId);
    if (mainButton) {
      new MutationObserver(sync).observe(mainButton, {
        attributes: true,
        attributeFilter: ['disabled'],
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    const realId = document.querySelector('#product-form-' + sectionId + ' input[name="id"]');
    if (realId) realId.addEventListener('change', sync);

    sync();
  }

  /* ----------------------------------------------------------------------
     3. Mobile gallery pagination dots
     ---------------------------------------------------------------------- */

  function initDots(dots) {
    if (dots.dataset.smaDotsReady === 'true') return;
    dots.dataset.smaDotsReady = 'true';

    const sliderComponent = dots.closest('slider-component');
    if (!sliderComponent) return;

    const buttons = Array.prototype.slice.call(dots.querySelectorAll('.sma-gallery-dot'));
    if (!buttons.length) return;

    // The dot count comes from Liquid's media_count, which can disagree with
    // the number of slides actually rendered (hidden variant images, a limit).
    // Reconcile in both directions so every slide has exactly one dot.
    const slideCount = sliderComponent.querySelectorAll('.slider__slide').length;
    if (slideCount) {
      while (buttons.length > slideCount) {
        buttons.pop().remove();
      }
      while (buttons.length < slideCount) {
        const extra = buttons[0].cloneNode(false);
        extra.classList.remove('is-active');
        extra.removeAttribute('aria-current');
        extra.dataset.index = String(buttons.length);
        extra.setAttribute('aria-label', String(buttons.length + 1));
        dots.appendChild(extra);
        buttons.push(extra);
      }
    }

    const setActive = function (index) {
      buttons.forEach(function (dot, i) {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };

    // Dawn's SliderComponent fires this whenever the visible slide changes,
    // covering swipe, arrows and programmatic moves alike.
    sliderComponent.addEventListener('slideChanged', function (event) {
      const page = event.detail && event.detail.currentPage;
      if (typeof page === 'number') setActive(page - 1);
    });

    // Clicking a dot scrolls the slider to that slide.
    dots.addEventListener('click', function (event) {
      const dot = event.target.closest('.sma-gallery-dot');
      if (!dot) return;

      const index = parseInt(dot.dataset.index, 10);
      const slider = sliderComponent.querySelector('.slider');
      if (!slider || isNaN(index)) return;

      const slides = slider.querySelectorAll('.slider__slide');
      if (!slides[index] || !slides[0]) return;

      slider.scrollTo({
        left: slides[index].offsetLeft - slides[0].offsetLeft,
        behavior: 'smooth',
      });
      setActive(index);
    });
  }

  /* ----------------------------------------------------------------------
     4. Size gate
     ----------------------------------------------------------------------
     Liquid already renders the gated state on first paint (see
     product-variant-picker / buy-buttons / sma-sticky-atc). This is the client
     side half: it remembers that a size *has* been chosen across Dawn's
     re-renders, and re-applies the gate whenever Dawn re-enables the button
     for a request that only carried a colour.

     "Chosen" is sticky per product-info element. It survives the variant-selects
     swap, and resets on a full product swap — which is correct, that's a
     different product. */

  var SELECT_SIZE_FALLBACK = 'Select Size';

  function selectSizeText() {
    return (window.variantStrings && window.variantStrings.selectSize) || SELECT_SIZE_FALLBACK;
  }

  function sizeGroups(productInfo) {
    var selects = productInfo.querySelector('variant-selects');
    if (!selects || !selects.hasAttribute('data-sma-size-option')) return null;

    var nodes = Array.prototype.slice.call(selects.querySelectorAll('[data-sma-size-input]'));
    if (!nodes.length) return null;

    var radios = [];
    var dropdowns = [];
    nodes.forEach(function (node) {
      if (node.tagName === 'INPUT') {
        radios.push(node);
      } else if (node.tagName === 'OPTION') {
        var parent = node.closest('select');
        if (parent && dropdowns.indexOf(parent) === -1) dropdowns.push(parent);
      }
    });

    return { root: selects, radios: radios, dropdowns: dropdowns };
  }

  // A dropdown always needs one option carrying `selected`, even while the gate
  // is on: VariantSelects.updateSelectionMetadata reads that option unguarded,
  // and a select with nothing selected would throw there and kill the change
  // handler. So the empty state is a real placeholder option.
  function dropdownPlaceholder(select) {
    var placeholder = select.querySelector('option[value=""]');
    if (placeholder) return placeholder;

    placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.disabled = true;
    placeholder.textContent = selectSizeText();
    select.insertBefore(placeholder, select.firstChild);
    return placeholder;
  }

  // Clears any server-rendered size selection. The server resolves a request to
  // the first available variant even when only a colour was sent, so the
  // matching size can come back marked as selected — this undoes that.
  function clearSizeSelection(groups) {
    groups.radios.forEach(function (radio) {
      radio.removeAttribute('checked');
      if (radio.checked) radio.checked = false;
    });

    groups.dropdowns.forEach(function (select) {
      var placeholder = dropdownPlaceholder(select);
      Array.prototype.forEach.call(select.options, function (option) {
        if (option !== placeholder) option.removeAttribute('selected');
      });
      placeholder.setAttribute('selected', 'selected');
      select.selectedIndex = placeholder.index;
    });
  }

  // Re-asserts the chosen size after a re-render. The server renders the picker
  // from option_values and can come back with nothing in the size row selected,
  // which would silently lose the shopper's choice.
  function restoreSizeSelection(groups, value) {
    if (!value) return;

    groups.radios.forEach(function (radio) {
      if (radio.value === value && !radio.checked) radio.checked = true;
    });

    groups.dropdowns.forEach(function (select) {
      var matched = null;
      Array.prototype.forEach.call(select.options, function (option) {
        if (option.value === value) matched = option;
        else option.removeAttribute('selected');
      });
      if (!matched) return;
      matched.setAttribute('selected', 'selected');
      select.selectedIndex = matched.index;
    });
  }

  function selectedVariant(productInfo) {
    var node = productInfo.querySelector('variant-selects [data-selected-variant]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  // True once the rendered variant is the one the chosen size belongs to. Until
  // then the form still holds the previous variant, so the button must stay
  // disabled — enabling early would let someone add the wrong variant.
  function serverHasChosenSize(productInfo) {
    var chosen = productInfo.smaSizeValue;
    if (!chosen) return false;

    var variant = selectedVariant(productInfo);
    if (!variant || !variant.available || !variant.options) return false;

    return variant.options.indexOf(chosen) !== -1;
  }

  function applyGate(productInfo) {
    var groups = sizeGroups(productInfo);
    if (!groups) return;

    var gated = productInfo.smaSizeChosen !== true;
    var sectionId = productInfo.dataset.section;

    groups.root.querySelectorAll('.sma-size-input').forEach(function (group) {
      group.classList.toggle('sma-size-input--unset', gated);
    });

    if (gated) clearSizeSelection(groups);
    else restoreSizeSelection(groups, productInfo.smaSizeValue);

    // Read by product-info.js updateURL: while gated there is no variant the
    // shopper actually picked, so ?variant= must stay out of the URL.
    if (gated) productInfo.dataset.smaSizeGateActive = 'true';
    else delete productInfo.dataset.smaSizeGateActive;

    var button = document.getElementById('ProductSubmitButton-' + sectionId);
    if (button) {
      var label = button.querySelector('span');

      // Also covers the dynamic checkout button, which submits the same form.
      var buttonsWrap = button.closest('.product-form__buttons');
      if (buttonsWrap) buttonsWrap.classList.toggle('sma-buttons--needs-size', gated);

      if (gated) {
        button.classList.add('sma-atc--needs-size');
        button.setAttribute('aria-disabled', 'true');
        if (!button.hasAttribute('disabled')) button.setAttribute('disabled', 'disabled');
        if (label && label.textContent.trim() !== selectSizeText()) label.textContent = selectSizeText();
      } else {
        button.classList.remove('sma-atc--needs-size');

        // Dawn mirrors the server's disabled state onto the button, so a render
        // that still carried the gate would leave it stuck. Repair it — but not
        // mid-submit, where aria-disabled is product-form.js's double-click
        // guard and clearing it would let the item be added twice.
        if (!button.classList.contains('loading') && serverHasChosenSize(productInfo)) {
          button.removeAttribute('aria-disabled');
          if (button.hasAttribute('disabled')) button.removeAttribute('disabled');
          var addToCart = window.variantStrings && window.variantStrings.addToCart;
          if (label && addToCart && label.textContent.trim() !== addToCart) {
            label.textContent = addToCart;
          }
        }
      }
    }

    // The sticky bar mirrors label and disabled state through syncSection; only
    // the gate's own styling and aria state need setting here.
    document
      .querySelectorAll('.sma-sticky-atc[data-section="' + sectionId + '"]')
      .forEach(function (bar) {
        bar.classList.toggle('sma-sticky-atc--needs-size', gated);

        var stickyButton = bar.querySelector('.sma-sticky-atc__button');
        if (!stickyButton) return;
        stickyButton.classList.toggle('sma-atc--needs-size', gated);
        if (gated) stickyButton.setAttribute('aria-disabled', 'true');
        else stickyButton.removeAttribute('aria-disabled');
      });
  }

  function initSizeGate(productInfo) {
    if (productInfo.dataset.smaSizeGateReady === 'true') return;
    if (!sizeGroups(productInfo)) return;
    productInfo.dataset.smaSizeGateReady = 'true';

    // Trust what Liquid rendered. It says "chosen" only when the request pinned
    // a variant down — a genuine ?variant= link, which does name a size. Dawn's
    // own replaceState can't fake that, because updateURL leaves ?variant= off
    // while the gate is on.
    var selects = productInfo.querySelector('variant-selects');
    productInfo.smaSizeChosen = !!(selects && selects.dataset.smaSizeChosen === 'true');

    if (productInfo.smaSizeChosen) {
      var preselected =
        productInfo.querySelector('input[data-sma-size-input]:checked') ||
        productInfo.querySelector('option[data-sma-size-input][selected]');
      if (preselected) productInfo.smaSizeValue = preselected.value;
    }

    // Capture phase, so the flag flips before VariantSelects kicks off its fetch
    // and before product-info.js decides what to write into the URL.
    productInfo.addEventListener(
      'change',
      function (event) {
        var target = event.target;
        if (!target) return;

        var isSize =
          (target.matches && target.matches('[data-sma-size-input]')) ||
          (target.tagName === 'SELECT' && target.querySelector('[data-sma-size-input]'));

        if (!isSize) return;
        if (target.tagName === 'SELECT' && !target.value) return;

        productInfo.smaSizeChosen = true;
        productInfo.smaSizeValue = target.value;
        applyGate(productInfo);
      },
      true
    );

    // Runs synchronously rather than on the next frame, so the server's gated
    // markup never gets a chance to paint over the real state.
    var applying = false;
    var observer = new MutationObserver(function () {
      if (applying) return;
      applying = true;
      try {
        applyGate(productInfo);
      } finally {
        applying = false;
      }
    });
    observer.observe(productInfo, { childList: true, subtree: true });

    applyGate(productInfo);
  }

  /* ----------------------------------------------------------------------
     5. Size Guide link
     ----------------------------------------------------------------------
     The size chart lives in a "Popup" block on the product template, whose
     trigger button is rendered as ProductPopup-<block id>. Clicking our link
     forwards to it so the existing modal-opener does all the work. The block id
     comes from Theme settings > SMA Product Page > Product popup ID, carried on
     the link itself. */

  var SIZE_GUIDE_FALLBACK_ID = 'ProductPopup-popup_n33Tz8';

  function openSizeGuide(trigger) {
    var id = trigger.getAttribute('data-sma-size-guide') || SIZE_GUIDE_FALLBACK_ID;
    var target = document.getElementById(id) || document.getElementById(SIZE_GUIDE_FALLBACK_ID);

    // Last resort: whatever popup block the template does have, nearest first,
    // so renaming or re-adding the block doesn't silently break the link.
    if (!target) {
      var scope = trigger.closest('product-info');
      if (scope) target = scope.querySelector('.product-popup-modal__button');
    }
    if (!target) target = document.querySelector('.product-popup-modal__button');
    if (target) target.click();
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest ? event.target.closest('[data-sma-size-guide]') : null;
    if (!trigger) return;
    event.preventDefault();
    openSizeGuide(trigger);
  });

  function init() {
    document.querySelectorAll('product-info').forEach(initSizeGate);

    document.querySelectorAll('[data-sma-product-section]').forEach(function (node) {
      // The marker element is replaced with the section, so its own flag is a
      // reliable "already wired" test. Without it, a theme-editor section load
      // would stack a second set of observers on the surviving price block and
      // submit button, and every variant change would run sync twice over.
      if (node.dataset.smaReady === 'true') return;
      node.dataset.smaReady = 'true';
      const id = node.getAttribute('data-sma-product-section');
      if (id) initSection(id);
    });

    document.querySelectorAll('[data-sma-gallery-dots]').forEach(initDots);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when the theme editor swaps the product section.
  document.addEventListener('shopify:section:load', init);

  // …and when Dawn swaps in a different product (combined listings), which
  // replaces the whole <product-info> and with it every marker we set.
  document.addEventListener('product-info:loaded', init);
})();